import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Building2,
  Calculator,
  Receipt,
  Shield,
  Globe,
  Plane,
  AlertTriangle,
  Landmark,
  Lightbulb,
  Users,
  GitMerge,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  MapPin,
} from "lucide-react";

const serviceData = {
  corporate: {
    icon: Building2,
    title: "Corporate Services in Bosnia and Herzegovina",
    subtitle: "Company Formation & Administration",
    description: "VTZ Backoffice provides corporate structuring and administrative support for companies establishing or operating businesses in Bosnia and Herzegovina.",
    services: [
      {
        title: "Company Formation in Bosnia and Herzegovina",
        description: "Complete company incorporation services including registration with local authorities, documentation and compliance setup for foreign investors.",
      },
      {
        title: "Branch Office Establishment",
        description: "Registration and setup of branch offices for foreign companies expanding into Bosnia and Herzegovina.",
      },
      {
        title: "Corporate Administration and Company Secretarial Services",
        description: "Ongoing corporate administration including registered office, document management and regulatory filings.",
      },
      {
        title: "Corporate Governance Support",
        description: "Implementation of governance frameworks, board support and compliance with local corporate governance requirements.",
      },
      {
        title: "Backoffice Administration for International Companies",
        description: "Comprehensive backoffice support for international companies operating in Bosnia and Herzegovina.",
      },
      {
        title: "Business Structuring for Foreign Investors",
        description: "Advisory on optimal business structures for foreign investors entering the Bosnian market.",
      },
      {
        title: "Family Office Services for Investors in Bosnia and Herzegovina",
        description: "Dedicated corporate services for family offices with investments in Bosnia and Herzegovina.",
      },
    ],
  },
  accounting: {
    icon: Calculator,
    title: "Accounting Services in Bosnia and Herzegovina",
    subtitle: "Financial Operations & Reporting",
    description: "We support financial operations and accounting management for companies operating in Bosnia and Herzegovina.",
    services: [
      {
        title: "Bookkeeping Services",
        description: "Comprehensive bookkeeping compliant with Bosnia and Herzegovina accounting standards and regulations.",
      },
      {
        title: "VAT Registration and VAT Compliance",
        description: "VAT registration, filing and compliance services in accordance with local tax authority requirements.",
      },
      {
        title: "Payroll Administration",
        description: "Full payroll processing including salary calculations, tax withholdings and social contributions.",
      },
      {
        title: "Financial Reporting",
        description: "Preparation of financial statements and management reports in accordance with local requirements.",
      },
      {
        title: "Audit Coordination with Local Audit Firms",
        description: "Coordination with local auditors, preparation of audit documentation and liaison support.",
      },
      {
        title: "Financial Administration for International Companies",
        description: "Day-to-day financial administration including accounts payable, receivable and treasury management.",
      },
      {
        title: "Company Liquidation and Dissolution Procedures",
        description: "Orderly company wind-down, liquidation procedures and dissolution filings with local authorities.",
      },
    ],
  },
  tax: {
    icon: Receipt,
    title: "Tax Services in Bosnia and Herzegovina",
    subtitle: "Tax Compliance & Advisory",
    description: "VTZ coordinates corporate and international tax compliance for companies operating in Bosnia and Herzegovina.",
    services: [
      {
        title: "Corporate Tax Advisory",
        description: "Strategic tax planning and advisory services for companies operating in Bosnia and Herzegovina.",
      },
      {
        title: "Personal Tax Planning for Executives",
        description: "Individual tax planning for executives and international employees working in Bosnia and Herzegovina.",
      },
      {
        title: "International Tax Structuring",
        description: "Design and implementation of tax-efficient structures for international operations.",
      },
      {
        title: "VAT Compliance and Advisory",
        description: "VAT planning, compliance and optimization services for businesses in Bosnia and Herzegovina.",
      },
      {
        title: "Transfer Pricing Coordination",
        description: "Transfer pricing documentation, benchmarking and policy implementation for cross-border transactions.",
      },
      {
        title: "Cross-Border Tax Structuring",
        description: "Tax optimization for cross-border transactions and investments involving Bosnia and Herzegovina.",
      },
    ],
  },
  trusts: {
    icon: Shield,
    title: "Trusts, Escrow & Fiduciary Services",
    subtitle: "Transaction Security & Fiduciary",
    description: "VTZ supports international investors with secure transaction structures and fiduciary arrangements related to investments in Bosnia and Herzegovina.",
    services: [
      {
        title: "Escrow Services for Corporate Transactions",
        description: "Independent escrow agent services for M&A transactions, real estate deals and investment agreements.",
      },
      {
        title: "Security Trustee Services",
        description: "Acting as security trustee for loan facilities, bond issues and secured transactions.",
      },
      {
        title: "Fiduciary Shareholding Structures",
        description: "Professional nominee shareholder services with full fiduciary responsibility.",
      },
      {
        title: "Asset Protection Structures",
        description: "Structures designed to protect investor assets in Bosnia and Herzegovina.",
      },
      {
        title: "Investment Holding Structures",
        description: "Design and administration of holding structures for investments in the region.",
      },
    ],
  },
  citizenship: {
    icon: Globe,
    title: "Citizenship & Residence in Bosnia and Herzegovina",
    subtitle: "Residence & Relocation Planning",
    description: "We assist international professionals, entrepreneurs and investors relocating to Bosnia and Herzegovina.",
    services: [
      {
        title: "Residence Permit Support for Foreign Nationals",
        description: "Comprehensive support for obtaining residence permits in Bosnia and Herzegovina.",
      },
      {
        title: "Business Residence for Company Owners",
        description: "Residence permits for business owners and company directors establishing operations.",
      },
      {
        title: "Long-Term Stay Planning",
        description: "Planning and coordination for long-term residence in Bosnia and Herzegovina.",
      },
      {
        title: "Digital Nomad Relocation to Bosnia and Herzegovina",
        description: "Relocation support for digital nomads and remote workers choosing Bosnia and Herzegovina.",
      },
      {
        title: "Citizenship by Naturalisation Support",
        description: "Long-term pathway support for those seeking citizenship through naturalisation.",
      },
    ],
  },
  relocation: {
    icon: Plane,
    title: "Business Relocation to Bosnia and Herzegovina",
    subtitle: "Corporate & Personal Relocation",
    description: "VTZ supports companies relocating operations or expanding to Bosnia and Herzegovina.",
    highlighted: true,
    services: [
      {
        title: "Personal Relocation Support",
        description: "End-to-end personal relocation support including housing, registration and local administration.",
      },
      {
        title: "Corporate Relocation Services",
        description: "Full-service corporate relocation including legal entity setup and regulatory compliance.",
      },
      {
        title: "Operational Setup for International Teams",
        description: "Infrastructure setup for international teams including HR, administration and compliance.",
      },
      {
        title: "Office Establishment Support",
        description: "Support for finding and establishing office space in Bosnia and Herzegovina.",
      },
      {
        title: "Hiring and Workforce Setup",
        description: "Recruitment coordination and workforce establishment for new market entrants.",
      },
    ],
  },
  compliance: {
    icon: AlertTriangle,
    title: "Risk & Compliance in Bosnia and Herzegovina",
    subtitle: "Regulatory & Risk Management",
    description: "We assist companies with regulatory compliance and governance requirements in Bosnia and Herzegovina.",
    services: [
      {
        title: "Regulatory Compliance Monitoring",
        description: "Ongoing monitoring of regulatory requirements and compliance obligations.",
      },
      {
        title: "GDPR Compliance Programs",
        description: "Data protection compliance including policies, procedures and implementation.",
      },
      {
        title: "AML / CFT Compliance Coordination",
        description: "Anti-money laundering and counter-terrorism financing compliance frameworks.",
      },
      {
        title: "Risk Management Frameworks",
        description: "Enterprise risk management design and implementation.",
      },
      {
        title: "Corporate Compliance Reporting",
        description: "Ongoing compliance monitoring, testing and reporting.",
      },
    ],
  },
  financial: {
    icon: Landmark,
    title: "Regulated Financial Services in Bosnia and Herzegovina",
    subtitle: "Financial Sector Entry",
    description: "VTZ assists financial institutions and fintech companies establishing operations in Bosnia and Herzegovina.",
    services: [
      {
        title: "Investment Services Structuring",
        description: "Structuring and setup support for investment services companies.",
      },
      {
        title: "Financial Institution Setup Support",
        description: "Support for establishing financial institutions in Bosnia and Herzegovina.",
      },
      {
        title: "Insurance Market Entry Support",
        description: "Assistance with entering the insurance market in Bosnia and Herzegovina.",
      },
      {
        title: "Funds and Investment Structure Coordination",
        description: "Coordination of fund structures and collective investment arrangements.",
      },
      {
        title: "Fintech Regulatory Coordination",
        description: "Regulatory guidance and coordination for fintech companies entering the market.",
      },
    ],
  },
  ip: {
    icon: Lightbulb,
    title: "Intellectual Property in Bosnia and Herzegovina",
    subtitle: "IP Protection & Management",
    description: "VTZ assists companies with protecting intellectual property in Bosnia and Herzegovina.",
    services: [
      {
        title: "Trademark Registration in Bosnia and Herzegovina",
        description: "National trademark registration and protection through the Institute for Intellectual Property.",
      },
      {
        title: "Patent Protection Procedures",
        description: "Patent filing and prosecution services in Bosnia and Herzegovina.",
      },
      {
        title: "Copyright Registration",
        description: "Copyright registration and protection strategies for creative works.",
      },
      {
        title: "Intellectual Property Portfolio Management",
        description: "Comprehensive management of IP portfolios including renewals and enforcement.",
      },
    ],
  },
  employment: {
    icon: Users,
    title: "Employment Advisory in Bosnia and Herzegovina",
    subtitle: "HR & Workforce Management",
    description: "We assist international companies with employment structures and workforce management in Bosnia and Herzegovina.",
    services: [
      {
        title: "Human Resources Advisory",
        description: "Strategic HR consulting including policies, procedures and best practices for the local market.",
      },
      {
        title: "Workforce Structuring",
        description: "Design of optimal workforce structures for international companies.",
      },
      {
        title: "Employment Law Coordination",
        description: "Employment law guidance including contract review and dispute support.",
      },
      {
        title: "Employment Contract Structuring",
        description: "Drafting and review of employment contracts compliant with local labor law.",
      },
      {
        title: "Executive Employment Planning",
        description: "Structuring of executive employment arrangements and compensation packages.",
      },
    ],
  },
  ma: {
    icon: GitMerge,
    title: "Mergers & Acquisitions in Bosnia and Herzegovina",
    subtitle: "M&A & Corporate Restructuring",
    description: "VTZ supports investors and companies through mergers, acquisitions and restructuring transactions involving companies in Bosnia and Herzegovina.",
    services: [
      {
        title: "M&A Transaction Coordination",
        description: "End-to-end coordination of merger and acquisition transactions in Bosnia and Herzegovina.",
      },
      {
        title: "Due Diligence Support",
        description: "Comprehensive due diligence coordination across legal, financial and operational areas.",
      },
      {
        title: "Corporate Restructuring",
        description: "Group restructuring, reorganizations and corporate simplification projects.",
      },
      {
        title: "Share Purchase Transaction Support",
        description: "Coordination and administration of share purchase processes and documentation.",
      },
      {
        title: "Investment Structuring",
        description: "Design of investment structures for private equity and strategic investors.",
      },
      {
        title: "Post-Merger Integration Support",
        description: "Operational support for post-merger integration and transition management.",
      },
    ],
  },
};

const ServiceDetailPage = () => {
  const { category } = useParams();
  const service = serviceData[category];

  const openCalendly = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: "https://calendly.com/franjo-vucic-d0pg/30min",
      });
    }
  };

  if (!service) {
    return (
      <div className="pt-32 pb-20 text-center">
        <h1 className="text-2xl font-semibold text-[#0B2A4A]">Service not found</h1>
        <Link to="/services" className="text-[#2F6FED] hover:underline mt-4 inline-block">
          Back to Services
        </Link>
      </div>
    );
  }

  const IconComponent = service.icon;

  return (
    <div data-testid={`service-detail-${category}`} className="pt-16 md:pt-20">
      {/* Hero Section */}
      <section className={`py-20 md:py-28 ${service.highlighted ? 'bg-gradient-to-b from-[#0B2A4A] to-[#0B2A4A]/95' : 'bg-gradient-to-b from-[#F5F7FA] to-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/services"
            className={`inline-flex items-center gap-2 text-sm font-medium mb-6 transition-colors ${
              service.highlighted ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-[#0B2A4A]'
            }`}
          >
            <ArrowLeft className="w-4 h-4" />
            All Services
          </Link>
          
          <div className="flex items-start gap-6 mb-6">
            <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${
              service.highlighted ? 'bg-[#2F6FED]' : 'bg-[#2F6FED]/10'
            }`}>
              <IconComponent className={`w-8 h-8 ${service.highlighted ? 'text-white' : 'text-[#2F6FED]'}`} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <MapPin className={`w-4 h-4 ${service.highlighted ? 'text-[#2F6FED]' : 'text-[#2F6FED]'}`} />
                <p className={`text-sm uppercase tracking-widest font-semibold ${
                  service.highlighted ? 'text-[#2F6FED]' : 'text-[#2F6FED]'
                }`}>
                  Bosnia and Herzegovina
                </p>
              </div>
              <h1 className={`text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1] font-['Sora'] ${
                service.highlighted ? 'text-white' : 'text-[#0B2A4A]'
              }`}>
                {service.title}
              </h1>
            </div>
          </div>
          
          <p className={`text-lg md:text-xl leading-relaxed max-w-3xl ${
            service.highlighted ? 'text-slate-300' : 'text-slate-600'
          }`}>
            {service.description}
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-2xl md:text-3xl font-semibold text-[#0B2A4A] font-['Sora']">
              Services Included
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {service.services.map((item, index) => (
              <Card
                key={index}
                data-testid={`service-item-${index}`}
                className="bg-white rounded-xl border border-slate-100 hover:border-slate-200 hover:shadow-md transition-all duration-300"
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-[#2F6FED]/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <CheckCircle className="w-4 h-4 text-[#2F6FED]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-[#0B2A4A] mb-2 font-['Sora']">
                        {item.title}
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Related Guide CTA */}
      <section className="py-16 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-100">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h3 className="text-xl font-semibold text-[#0B2A4A] mb-2 font-['Sora']">
                  Learn More About Doing Business in BiH
                </h3>
                <p className="text-slate-600">
                  Our comprehensive guide covers everything you need to know about operating in Bosnia and Herzegovina.
                </p>
              </div>
              <Link to="/doing-business-bosnia">
                <Button className="bg-[#0B2A4A] hover:bg-[#0B2A4A]/90 text-white font-medium px-6 py-3 rounded-md whitespace-nowrap">
                  Read the Guide
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#0B2A4A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-6 font-['Sora']">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Contact our team to discuss your {service.subtitle.toLowerCase()} requirements 
            in Bosnia and Herzegovina.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={openCalendly}
              className="bg-[#2F6FED] hover:bg-[#2558be] text-white font-medium px-8 py-4 rounded-md transition-all duration-200"
            >
              Schedule a Consultation
            </Button>
            <Link to="/contact">
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#0B2A4A] font-medium px-8 py-4 rounded-md transition-all duration-200"
              >
                Contact Us
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;
